import api from "../config/axios";
import { logger } from "../utils/logger";

const getApiErrorStatus = (error: unknown): number | undefined => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }
  return undefined;
};

export interface CalculoValorTotalParams {
  entrada: string;
  salida: string;
  departamento: string;
  late_checkout?: boolean;
  precio_personalizado?: number;
  usar_precio_base?: boolean;
}

export interface CalculoValorTotalResult {
  valor_base: number;
  recargo_late: number;
  valor_total: number;
  precio_por_noche: number;
  noches: number;
  late_checkout: boolean;
}

export interface CalculoSaldoPendienteParams {
  valor_total: number;
  pago: number;
  ajuste_financiero?: number;
}

export interface CalculoSaldoPendienteResult {
  valor_total: number;
  pago: number;
  ajuste_financiero: number;
  saldo_pendiente: number;
}

export interface CalculoPrecioPorNocheParams {
  valor_total: number;
  entrada: string;
  salida: string;
}

export interface CalculoPrecioPorNocheResult {
  valor_total: number;
  noches: number;
  precio_por_noche: number;
}

export class CalculationService {
  /**
   * Calcula el valor total de una reserva usando el backend
   * Reemplaza los cálculos del frontend
   */
  static async calcularValorTotal(
    params: CalculoValorTotalParams
  ): Promise<CalculoValorTotalResult> {
    try {
      const response = await api.post("/reservas/calcular-valor-total", params);
      return response.data;
    } catch (error: unknown) {
      // No mostrar errores 400 en consola ya que pueden ser esperados
      // (datos incompletos durante la edición del formulario)
      if (getApiErrorStatus(error) !== 400) {
        logger.errorWithContext("Error al calcular valor total", error, {
          params,
        });
      }
      throw error;
    }
  }

  /**
   * Calcula el saldo pendiente usando el backend
   * Reemplaza los cálculos del frontend
   */
  static async calcularSaldoPendiente(
    params: CalculoSaldoPendienteParams
  ): Promise<CalculoSaldoPendienteResult> {
    try {
      const response = await api.post(
        "/reservas/calcular-saldo-pendiente",
        params
      );
      return response.data;
    } catch (error) {
      logger.errorWithContext("Error al calcular saldo pendiente", error, {
        params,
      });
      throw error;
    }
  }

  /**
   * Calcula el precio por noche usando el backend
   * Reemplaza los cálculos del frontend
   */
  static async calcularPrecioPorNoche(
    params: CalculoPrecioPorNocheParams
  ): Promise<CalculoPrecioPorNocheResult> {
    try {
      const response = await api.post(
        "/reservas/calcular-precio-por-noche",
        params
      );
      return response.data;
    } catch (error) {
      logger.errorWithContext("Error al calcular precio por noche", error, {
        params,
      });
      throw error;
    }
  }

  /**
   * Calcula el número de noches entre dos fechas
   * Esta función puede quedarse en el frontend ya que es solo para display
   */
  static calcularNoches(entrada: Date | null, salida: Date | null): number {
    if (!entrada || !salida) return 0;
    const diferenciaMs = salida.getTime() - entrada.getTime();
    return Math.max(1, Math.round(diferenciaMs / (1000 * 60 * 60 * 24)));
  }

  /**
   * Formatea un valor monetario para display
   * Esta función puede quedarse en el frontend ya que es solo para display
   */
  static formatearMoneda(valor: number): string {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valor);
  }
}
